import { useEffect, useRef, useState } from 'react';
import type { Screen, DensityPreset, TrainSettings } from '../types';
import { useGameStore } from '../store';
import { ParallaxBg } from './ParallaxBg';
import { playClick, playToggle } from '../sounds';
import { generatePuzzleAsync } from '../trainPuzzleService';
import {
  MAX_TRAIN_SEED_LENGTH,
  TRAIN_SEED_VALIDATION_DELAY_MS,
  hasConfiguredTrainSeed,
  nextTrainSeed,
  normalizeTrainSeedInput,
} from '../trainSeed';
import { Muted, Button, Chip, Divider, Toggle, MultiToggle } from './ui';
import practiceLogo from '../../assets/practice-logo.png';
import './LabScreen.css';

const SIZE_OPTIONS = [
  { value: 5, label: '5\u00d75' },
  { value: 10, label: '10\u00d710' },
  { value: 15, label: '15\u00d715' },
  { value: 20, label: '20\u00d720' },
  { value: 25, label: '25\u00d725' },
  { value: 30, label: '30\u00d730' },
];

const SIZE_SUBTITLES: Record<number, string> = {
  5: 'Fast warmup',
  10: 'Balanced',
  15: 'Long run',
  20: 'Big challenge',
  25: 'Huge board',
  30: 'Impossible',
};

const DENSITY_OPTIONS = [
  { value: 'sparse' as DensityPreset, label: 'Sparse' },
  { value: 'normal' as DensityPreset, label: 'Normal' },
  { value: 'dense' as DensityPreset, label: 'Dense' },
];

const DENSITY_SUBTITLES: Record<string, string> = {
  sparse: 'Low fill ratio',
  normal: 'Balanced fill',
  dense: 'High fill ratio',
};

const LEVEL_RULE_OPTIONS: {
  key: 'strict';
  label: string;
  activeSubtitle: string;
  inactiveSubtitle: string;
}[] = [
  {
    key: 'strict',
    label: 'Strict',
    activeSubtitle: 'Mistakes on',
    inactiveSubtitle: 'Mistakes off',
  },
];

const COUNT_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: -1, label: '∞' },
];

const PREFERENCE_OPTIONS: {
  storeKey: 'timerVisible' | 'cellStateCycleEnabled' | 'spanHintsEnabled';
  toggleKey: 'toggleTimerVisible' | 'toggleCellStateCycle' | 'toggleSpanHints';
  label: string;
  activeSubtitle: string;
  inactiveSubtitle: string;
}[] = [
  {
    storeKey: 'timerVisible',
    toggleKey: 'toggleTimerVisible',
    label: 'Show Timer',
    activeSubtitle: 'Display elapsed time',
    inactiveSubtitle: 'Display elapsed time',
  },
  {
    storeKey: 'cellStateCycleEnabled',
    toggleKey: 'toggleCellStateCycle',
    label: 'Cycle Cell States',
    activeSubtitle: 'Tap cycles through states',
    inactiveSubtitle: 'Tap cycles through states',
  },
  {
    storeKey: 'spanHintsEnabled',
    toggleKey: 'toggleSpanHints',
    label: 'Span Hints',
    activeSubtitle: 'Show space each clue needs',
    inactiveSubtitle: 'Show space each clue needs',
  },
];

function getDensityLabel(value: DensityPreset): string {
  return (
    DENSITY_OPTIONS.find((opt) => opt.value === value)?.label.toLowerCase() ??
    value
  );
}

function getSeedCheckingMessage(size: number, density: DensityPreset): string {
  return `Checking if seed is unique for ${size}x${size} ${getDensityLabel(
    density
  )}...`;
}

interface LabScreenProps {
  onNavigate: (screen: Screen) => void;
}

type SeedValidationState =
  | { status: 'checking'; seed: string }
  | { status: 'valid'; seed: string }
  | { status: 'invalid'; message: string };

function getSeedValidationErrorMessage(error: unknown): string {
  if (
    error instanceof Error &&
    /Failed to generate a uniqueness-proven/i.test(error.message)
  ) {
    return 'This seed was rejected for the current size and density.';
  }

  return 'Seed validation failed. Try another seed.';
}

export function LabScreen({ onNavigate }: LabScreenProps) {
  const storeSettings = useGameStore((s) => s.trainSettings);
  const store = useGameStore();
  const [settings, setSettings] = useState<TrainSettings>(() => storeSettings);
  const [seedInput, setSeedInput] = useState(() => storeSettings.seed);
  const seedInputRef = useRef(seedInput);
  const seedGenerationRequestIdRef = useRef(0);
  const skipNextEffectValidationRef = useRef(false);
  const [debouncedSeedInput, setDebouncedSeedInput] = useState(
    () => storeSettings.seed
  );
  const [seedValidation, setSeedValidation] = useState<SeedValidationState>(
    () =>
      hasConfiguredTrainSeed(storeSettings.seed)
        ? { status: 'checking', seed: storeSettings.seed }
        : { status: 'invalid', message: 'Enter a seed or generate a new one.' }
  );
  const [isGeneratingSeed, setIsGeneratingSeed] = useState(false);
  seedInputRef.current = seedInput;

  const update = (patch: Partial<TrainSettings>) => {
    if (patch.size != null || patch.density != null) {
      seedGenerationRequestIdRef.current += 1;
      setIsGeneratingSeed(false);
      skipNextEffectValidationRef.current = false;
    }

    setSettings((prev) => ({ ...prev, ...patch }));
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSeedInput(seedInput);
    }, TRAIN_SEED_VALIDATION_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [seedInput]);

  useEffect(() => {
    if (isGeneratingSeed) {
      return;
    }

    const normalizedSeed = normalizeTrainSeedInput(debouncedSeedInput);

    if (!normalizedSeed) {
      skipNextEffectValidationRef.current = false;
      setSeedValidation({
        status: 'invalid',
        message: 'Enter a seed or generate a new one.',
      });
      return;
    }

    if (skipNextEffectValidationRef.current) {
      skipNextEffectValidationRef.current = false;
      return;
    }

    let cancelled = false;
    setSeedValidation({ status: 'checking', seed: normalizedSeed });

    generatePuzzleAsync(settings.size, normalizedSeed, settings.density)
      .then(() => {
        if (cancelled) return;
        if (normalizeTrainSeedInput(seedInputRef.current) !== normalizedSeed) {
          return;
        }

        setSeedValidation({ status: 'valid', seed: normalizedSeed });
        setSeedInput((current) =>
          normalizeTrainSeedInput(current) === normalizedSeed
            ? normalizedSeed
            : current
        );
        setSettings((prev) =>
          prev.seed === normalizedSeed
            ? prev
            : { ...prev, seed: normalizedSeed }
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;

        setSeedValidation({
          status: 'invalid',
          message: getSeedValidationErrorMessage(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSeedInput, isGeneratingSeed, settings.density, settings.size]);

  const handleSeedChange = (value: string) => {
    const nextSeedInput = value.slice(0, MAX_TRAIN_SEED_LENGTH);
    seedGenerationRequestIdRef.current += 1;
    setIsGeneratingSeed(false);
    skipNextEffectValidationRef.current = false;
    setSeedInput(nextSeedInput);

    if (hasConfiguredTrainSeed(nextSeedInput)) {
      setSeedValidation({
        status: 'checking',
        seed: normalizeTrainSeedInput(nextSeedInput),
      });
      return;
    }

    setSeedValidation({
      status: 'invalid',
      message: 'Enter a seed or generate a new one.',
    });
  };

  const handleGenerateSeed = async () => {
    const requestId = seedGenerationRequestIdRef.current + 1;
    seedGenerationRequestIdRef.current = requestId;
    skipNextEffectValidationRef.current = false;
    setIsGeneratingSeed(true);

    let currentSeed =
      normalizeTrainSeedInput(seedInputRef.current) || settings.seed;

    // Show a few seeds cycling for visual effect, then settle on the final one.
    const CYCLE_FRAMES = 6;
    for (let frame = 0; frame < CYCLE_FRAMES; frame++) {
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (seedGenerationRequestIdRef.current !== requestId) return;
      const candidate = nextTrainSeed(currentSeed);
      currentSeed = candidate;
      setSeedInput(candidate);
      setSeedValidation({ status: 'checking', seed: candidate });
    }

    if (seedGenerationRequestIdRef.current !== requestId) return;

    // With constructive generation, every seed produces a valid puzzle.
    // Generate it in the worker so the result is cached for play.
    const candidate = currentSeed;
    try {
      await generatePuzzleAsync(settings.size, candidate, settings.density);
      if (seedGenerationRequestIdRef.current !== requestId) return;

      setSeedInput(candidate);
      setSeedValidation({ status: 'valid', seed: candidate });
      setSettings((prev) =>
        prev.seed === candidate ? prev : { ...prev, seed: candidate }
      );
      skipNextEffectValidationRef.current = true;
    } catch (error) {
      if (seedGenerationRequestIdRef.current !== requestId) return;
      setSeedValidation({
        status: 'invalid',
        message: getSeedValidationErrorMessage(error),
      });
    }

    setIsGeneratingSeed(false);
  };

  const canPlay = seedValidation.status === 'valid' && !isGeneratingSeed;

  const handlePlay = () => {
    if (seedValidation.status !== 'valid') return;

    const nextSettings = { ...settings, seed: seedValidation.seed };
    useGameStore.getState().saveTrainSettings(nextSettings);

    onNavigate({
      type: 'level',
      mode: 'train',
      size: nextSettings.size,
      seed: seedValidation.seed,
      density: nextSettings.density,
      strict: nextSettings.strict,
      hintCount: nextSettings.hintCount,
      validateCount: nextSettings.validateCount,
      autoMark: nextSettings.autoMark,
      entry: 'fresh',
    });
  };

  return (
    <div className="train-screen">
      <ParallaxBg className="game-bg" />

      <img
        className="train-logo"
        src={practiceLogo}
        alt="Nonogram Practice"
        draggable={false}
      />
      <Muted as="p" className="train-subtitle">
        Practice runs with your own rules and seed.
      </Muted>

      <div className="train-options-panel">
        <div className="train-panel-header">
          <div className="train-panel-eyebrow">Level Setup</div>
        </div>

        <div className="train-option-stack">
          <div className="train-option-row">
            <div className="train-option-copy">
              <span className="train-option-label">Grid</span>
              <span className="train-option-help-inline">
                Board size and fill density.
              </span>
            </div>
          </div>
          <div
            className="train-size-pills train-size-pills--sizes"
            role="group"
            aria-label="Board size"
          >
            {SIZE_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                active={settings.size === opt.value}
                className="train-size-pill"
                onClick={() => {
                  playClick();
                  update({ size: opt.value });
                }}
              >
                <span className="train-size-pill-grid">{opt.label}</span>
                <span className="train-size-pill-label">{SIZE_SUBTITLES[opt.value]}</span>
              </Chip>
            ))}
          </div>
          <div className="train-count-rows">
            <div className="train-count-row">
              <div className="train-toggle-info">
                <span className="train-count-label">Density</span>
                <span className="train-toggle-desc">{DENSITY_SUBTITLES[settings.density]}</span>
              </div>
              <MultiToggle
                options={DENSITY_OPTIONS}
                value={settings.density}
                onChange={(v) => {
                  playClick();
                  update({ density: v });
                }}
                label="Density"
              />
            </div>
          </div>
        </div>

        <Divider className="train-option-divider" />

        <div className="train-option-stack">
          <div className="train-option-row">
            <div className="train-option-copy">
              <span className="train-option-label">Preferences</span>
              <span className="train-option-help-inline">
                Adjustable any time, even mid-game.
              </span>
            </div>
          </div>
          <div className="train-toggle-rows">
            {PREFERENCE_OPTIONS.map((opt) => {
              const active = store[opt.storeKey];
              const toggle = store[opt.toggleKey];

              return (
                <div key={opt.storeKey} className="train-toggle-row">
                  <div className="train-toggle-info">
                    <span className="train-toggle-label">{opt.label}</span>
                    <span className="train-toggle-desc">
                      {active ? opt.activeSubtitle : opt.inactiveSubtitle}
                    </span>
                  </div>
                  <Toggle
                    checked={active}
                    onChange={() => {
                      playToggle();
                      toggle();
                    }}
                  />
                </div>
              );
            })}
            {/* Clue Progress uses trainSettings, not global store */}
            <div className="train-toggle-row">
              <div className="train-toggle-info">
                <span className="train-toggle-label">Clue Progress</span>
                <span className="train-toggle-desc">
                  Gray out completed clues
                </span>
              </div>
              <Toggle
                checked={settings.autoMark}
                onChange={() => {
                  playToggle();
                  update({ autoMark: !settings.autoMark });
                }}
              />
            </div>
          </div>
        </div>

        <Divider className="train-option-divider" />

        <div className="train-option-stack">
          <div className="train-option-row">
            <div className="train-option-copy">
              <span className="train-option-label">Seed</span>
              <span className="train-option-help-inline">
                Use any text seed to replay the same generated puzzle.
              </span>
            </div>
          </div>
          <div className="train-seed-control">
            <div className="train-seed-input-row">
              <input
                className={`train-seed-input${
                  seedValidation.status === 'valid' ? ' is-valid' : ''
                }${seedValidation.status === 'invalid' ? ' is-invalid' : ''}${
                  seedValidation.status === 'checking' ? ' is-checking' : ''
                }`}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={MAX_TRAIN_SEED_LENGTH}
                value={seedInput}
                onChange={(event) => handleSeedChange(event.target.value)}
              />
              <Button
                variant="secondary"
                size="custom"
                className="train-seed-generate-btn"
                disabled={isGeneratingSeed}
                onClick={() => {
                  playClick();
                  void handleGenerateSeed();
                }}
              >
                {isGeneratingSeed ? 'Validating...' : 'New Seed'}
              </Button>
            </div>
            <div className="train-seed-feedback">
              {isGeneratingSeed && (
                <span
                  className="train-seed-status train-seed-status-checking"
                  role="status"
                  aria-live="polite"
                >
                  {getSeedCheckingMessage(settings.size, settings.density)}
                </span>
              )}
              {seedValidation.status === 'valid' && (
                <span
                  className="train-seed-status train-seed-status-valid"
                  role="status"
                  aria-live="polite"
                >
                  <span className="train-seed-checkmark" aria-hidden="true">
                    ✓
                  </span>
                  Seed verified unique
                </span>
              )}
              {!isGeneratingSeed && seedValidation.status === 'checking' && (
                <span
                  className="train-seed-status train-seed-status-checking"
                  role="status"
                  aria-live="polite"
                >
                  {getSeedCheckingMessage(settings.size, settings.density)}
                </span>
              )}
              {!isGeneratingSeed && seedValidation.status === 'invalid' && (
                <span
                  className="train-seed-status train-seed-status-invalid"
                  role="alert"
                >
                  {seedValidation.message}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <Button
        variant="primary"
        size="wide"
        className="train-play-btn"
        disabled={!canPlay}
        onClick={() => {
          playClick();
          handlePlay();
        }}
      >
        Play
      </Button>
    </div>
  );
}
